import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { firstValueFrom, Subject, Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { DashboardStats } from '../response-models/dashboard-stats.model';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';
import { OrganizationContextService } from '../services/organization-context.service';

/** Auto-refresh interval for the stats (60 seconds). */
const AUTO_REFRESH_INTERVAL = 60000;

/**
 * Dashboard stats: `GET /organizations/:orgId/dashboard` (hub `user-dashboard-controller.ts`), cached as
 * signal state and auto-refreshed every minute while the dashboard is open.
 * The activity feed lives in `OrganizationActivityService`.
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardService extends ApiService implements OnDestroy {
  private organizationContext = inject(OrganizationContextService);
  private destroy$ = new Subject<void>();
  private autoRefreshSubscription: Subscription | null = null;

  private statsSignal = signal<DashboardStats | null>(null);
  private loadingStatsSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly stats$ = computed(() => this.statsSignal());
  readonly isLoadingStats$ = computed(() => this.loadingStatsSignal());
  readonly error$ = computed(() => this.errorSignal());

  constructor() {
    super('dashboard');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoRefresh();
  }

  /** Load the stats, then keep them fresh with auto-refresh. */
  async initialize(): Promise<void> {
    if (!this.organizationContext.currentOrganizationId()) {
      console.warn('[DashboardService] No organization selected');
      return;
    }
    await this.loadStats();
    this.startAutoRefresh();
  }

  /** Load the current organization's stats. Failures set `error$` and resolve `null`. */
  async loadStats(): Promise<DashboardStats | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[DashboardService] No organization selected');
      return null;
    }

    this.loadingStatsSignal.set(true);
    this.errorSignal.set(null);
    try {
      const stats = await firstValueFrom(this.request('GET', { prefix: ['organizations', orgId] }, toDashboardStats));
      this.statsSignal.set(stats);
      return stats;
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      console.error('[DashboardService] Error loading stats:', apiError.message);
      this.errorSignal.set(apiError.errors[0] ?? 'Failed to load dashboard stats');
      return null;
    } finally {
      this.loadingStatsSignal.set(false);
    }
  }

  /** Refresh stats (manual refresh) */
  async refreshStats(): Promise<void> {
    await this.loadStats();
  }

  /** Start auto-refresh for stats */
  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.autoRefreshSubscription = interval(AUTO_REFRESH_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => void this.loadStats());
  }

  /** Stop auto-refresh */
  stopAutoRefresh(): void {
    this.autoRefreshSubscription?.unsubscribe();
    this.autoRefreshSubscription = null;
  }

  /** Clear error state */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /** Get stats synchronously */
  getStats(): DashboardStats | null {
    return this.statsSignal();
  }
}

function toDashboardStats(data: unknown): DashboardStats {
  const source = isRecord(data) ? data : {};
  return {
    totalDrones: countOrZero(source['totalDrones']),
    activeDrones: countOrZero(source['activeDrones']),
    flightHours: countOrZero(source['flightHours']),
    totalAcresSprayed: countOrZero(source['totalAcresSprayed']),
    upcomingFlights: countOrZero(source['upcomingFlights']),
  };
}
